<script lang="ts">
  import { projectStore } from '$lib/state/project.svelte';
  import {
    buildRecipePayload,
    decodeRecipe,
    encodeRecipe,
    RecipeError,
  } from '$lib/io/recipe';
  import { applyRecipe } from '$lib/io/recipe-apply.svelte';

  interface Props {
    onMessage?: (m: { kind: 'warn' | 'error' | 'info'; text: string }) => void;
  }
  const { onMessage }: Props = $props();

  let open = $state(false);
  let mode = $state<'menu' | 'copy' | 'paste'>('menu');
  let copiedText = $state('');
  let copiedId = $state('');
  let pasteText = $state('');

  async function openCopy() {
    mode = 'copy';
    open = true;
    try {
      const payload = buildRecipePayload(
        $state.snapshot(projectStore.settings),
        $state.snapshot(projectStore.pipeline),
        $state.snapshot(projectStore.layer_order),
      );
      const r = await encodeRecipe(payload);
      copiedText = r.text;
      copiedId = r.id;
    } catch (err) {
      onMessage?.({
        kind: 'error',
        text: err instanceof Error ? err.message : 'Failed to build recipe.',
      });
      open = false;
    }
  }

  function openPaste() {
    mode = 'paste';
    pasteText = '';
    open = true;
  }

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(copiedText);
      onMessage?.({ kind: 'info', text: 'Recipe copied to clipboard.' });
    } catch {
      onMessage?.({
        kind: 'warn',
        text: 'Clipboard blocked — select and copy the text manually.',
      });
    }
  }

  async function applyPaste() {
    try {
      const payload = await decodeRecipe(pasteText);
      applyRecipe(payload);
      onMessage?.({ kind: 'info', text: 'Recipe applied to current image.' });
      open = false;
    } catch (err) {
      const msg =
        err instanceof RecipeError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to apply recipe.';
      onMessage?.({ kind: 'error', text: msg });
    }
  }

  function close() {
    open = false;
    mode = 'menu';
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') close();
  }
</script>

<svelte:window onkeydown={onKey} />

<div class="wrap">
  <button
    class="trigger"
    onclick={() => {
      if (!open) {
        mode = 'menu';
        open = true;
      } else {
        close();
      }
    }}
    aria-haspopup="menu"
    aria-expanded={open}
  >
    Recipe ▾
  </button>

  {#if open}
    <div class="popover" role="dialog" aria-modal="true">
      {#if mode === 'menu'}
        <button class="item" onclick={openCopy} disabled={!projectStore.input_asset}>
          Copy current recipe…
        </button>
        <button class="item" onclick={openPaste}>
          Paste &amp; apply recipe…
        </button>
      {:else if mode === 'copy'}
        <div class="head">
          <span>Recipe ID:</span>
          <code>{copiedId || '—'}</code>
        </div>
        <textarea readonly class="code" rows="6">{copiedText}</textarea>
        <div class="row">
          <button onclick={copyToClipboard} class="primary">
            Copy to clipboard
          </button>
          <button onclick={close}>close</button>
        </div>
        <p class="hint">
          Paste this string in another session to re-apply this pipeline to a
          different image. The recipe does not include the source picture.
        </p>
      {:else if mode === 'paste'}
        <div class="head">
          <span>Paste a recipe string</span>
        </div>
        <textarea
          bind:value={pasteText}
          class="code"
          rows="6"
          placeholder="paste recipe here"
          spellcheck="false"
        ></textarea>
        <div class="row">
          <button onclick={applyPaste} class="primary" disabled={!pasteText.trim()}>
            Apply
          </button>
          <button onclick={close}>cancel</button>
        </div>
        <p class="hint">
          The recipe replaces your project settings and pipeline. Your current
          upload stays. Rotation from the recipe is applied — change it back in
          the input panel if undesired.
        </p>
      {/if}
    </div>
  {/if}
</div>

<style>
  .wrap {
    position: relative;
  }
  .trigger {
    font-size: 12px;
    padding: 6px 10px;
  }
  .popover {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 6px;
    background: var(--surface);
    border: 1px solid var(--accent-dim);
    border-radius: 6px;
    padding: 10px;
    width: 380px;
    z-index: 100;
    display: flex;
    flex-direction: column;
    gap: 8px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  }
  .item {
    text-align: left;
    background: transparent;
    border: 1px solid transparent;
    padding: 8px 10px;
    font-size: 13px;
    border-radius: 3px;
  }
  .item:hover:not(:disabled) {
    background: var(--surface-2);
    border-color: var(--border);
  }
  .item:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    font-size: 12px;
    color: var(--text-dim);
  }
  .head code {
    font-family: var(--font-mono);
    color: var(--accent);
  }
  .code {
    font-family: var(--font-mono);
    font-size: 11px;
    background: var(--bg);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 6px;
    resize: vertical;
    word-break: break-all;
    white-space: pre-wrap;
  }
  .row {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
  }
  .row button.primary {
    background: var(--accent-dim);
    border-color: var(--accent);
    color: var(--bg);
    font-weight: 600;
  }
  .hint {
    margin: 0;
    color: var(--text-dim);
    font-size: 11px;
  }
</style>
